import { Dispatch, SetStateAction, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  useTheme,
} from '@mui/material'
import { useNavigate } from 'react-router-dom'

import { UserAvatar } from 'src/components/users-list/table-view/user-avatar.component'
import { AccountOwnerChangeModal } from '../account-owner-change'
import {
  AccountPricesModal,
  AccountSubscriptionGift,
  CustomCssModal,
  HelperWrapper,
  MoreTableButton,
  SubscriptionBadge,
} from 'src/components'
import {
  useMe,
  useUpdateAccountSuspension,
  useUpdateAccountEnterprise,
  useUpdateAccountPublicStatus,
} from 'src/hooks'
import { EAccountSubscriptionPlans, IAccountsAllResp } from 'src/types'

import { getTableTimestamp, joinAbsRoute } from 'src/common/utils'
import { ROUTES } from 'src/constants'
import { WhiteLabelModal } from './account-white-label-modal/white-label-modal.component'
import { PublicSwitch } from 'src/components/products/products-table/public-switch'

interface IAccountsTableProps {
  data: IAccountsAllResp[]
  sort: string
  setSort: Dispatch<SetStateAction<string>>
}

const AccountsTable = ({ data, sort, setSort }: IAccountsTableProps) => {
  const [t] = useTranslation()
  const [roleChangeId, setRoleChangeId] = useState<string | null>(null)
  const [subscriptionGiftId, setSubscriptionGiftId] = useState<string | null>(null)
  const [customCssId, setCustomCssId] = useState<string | null>(null)
  const [pricesId, setPricesId] = useState<string | null>(null)
  const [modalOpen, setModelOpen] = useState(false)
  const [currentRow, setCurrentRow] = useState<string>()

  const navigate = useNavigate()
  const theme = useTheme()
  const { data: user } = useMe()
  const { mutateAsync: updateSuspension } = useUpdateAccountSuspension()
  const { mutateAsync: updateEnterprise } = useUpdateAccountEnterprise()
  const { mutateAsync: updatePublickStatus } = useUpdateAccountPublicStatus()

  const handleRoleChangeOpen = (id: string) => () => setRoleChangeId(id)
  const handleRoleChangeClose = () => setRoleChangeId(null)
  const handleSubscriptionGiftOpen = (id: string) => () => setSubscriptionGiftId(id)
  const handleSubscriptionGiftClose = () => setSubscriptionGiftId(null)
  const handleCustomCssOpen = (id: string) => () => setCustomCssId(id)
  const handleCustomCssClose = () => setCustomCssId(null)
  const handlePricesOpen = (id: string) => () => setPricesId(id)
  const handlePricesClose = () => setPricesId(null)

  const handleSortChange = (sortName: string) => () => {
    setSort((prevSort) =>
      prevSort.split('-')[1] === 'asc' ? `${sortName}-desc` : `${sortName}-asc`,
    )
  }

  const handleAccountClick = (id: string) => () => {
    navigate(joinAbsRoute(ROUTES.ACCOUNTS, id, ROUTES.DASHBOARD))
  }

  const handleSuspension = (id: string, suspended: boolean) => async () => {
    await updateSuspension({ id, suspended: !suspended })
  }

  const handleEnterprise = (id: string, assign: boolean) => async () => {
    await updateEnterprise({ id, assign: !assign })
  }

  const handleModalOpen = (id: string) => () => {
    setCurrentRow(id)
    setModelOpen(true)
  }

  const handleModalClose = () => {
    setCurrentRow(undefined)
    setModelOpen(false)
  }

  const handlePublicChange = (id: string, publicStatus: boolean) => {
    updatePublickStatus({ id, public: publicStatus })
  }

  return (
    <>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow sx={{ 'td, th': { border: 0 } }}>
              <TableCell align="center" />
              <TableCell align="center">
                <TableSortLabel
                  onClick={handleSortChange('account_holder')}
                  active={sort.split('-')[0] === 'account_holder'}
                  direction={sort.split('-')[1] as 'desc' | 'asc'}
                  sx={{
                    marginLeft: '20px',
                  }}
                >
                  <HelperWrapper t="accounts-table-account-name-column" />
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">
                <TableSortLabel
                  onClick={handleSortChange('email')}
                  active={sort.split('-')[0] === 'email'}
                  direction={sort.split('-')[1] as 'desc' | 'asc'}
                  sx={{
                    marginLeft: '20px',
                  }}
                >
                  <HelperWrapper t="accounts-table-owner-email-column" />
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">
                <TableSortLabel
                  onClick={handleSortChange('created_at')}
                  active={sort.split('-')[0] === 'created_at'}
                  direction={sort.split('-')[1] as 'desc' | 'asc'}
                  sx={{
                    marginLeft: '20px',
                  }}
                >
                  <HelperWrapper t="table-created-column" />
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">
                <TableSortLabel
                  onClick={handleSortChange('updated_at')}
                  active={sort.split('-')[0] === 'updated_at'}
                  direction={sort.split('-')[1] as 'desc' | 'asc'}
                  sx={{
                    marginLeft: '20px',
                  }}
                >
                  <HelperWrapper t="table-modified-column" />
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">
                <TableSortLabel
                  onClick={handleSortChange('public')}
                  active={sort.split('-')[0] === 'public'}
                  direction={sort.split('-')[1] as 'desc' | 'asc'}
                  sx={{
                    marginLeft: '20px',
                  }}
                >
                  <HelperWrapper t={'table-public-column'} />
                </TableSortLabel>
              </TableCell>
              <TableCell align="center" />
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row) => (
              <TableRow
                onClick={handleAccountClick(row.id)}
                key={row.id}
                sx={{
                  borderTop: `1px solid ${theme.palette.custom.grey}`,
                  borderBottom: `1px solid ${theme.palette.custom.grey}`,
                  cursor: 'pointer',
                  'td, th': { border: 0 },
                  bgcolor: row.ai_generated
                    ? theme.palette.custom.lightGreen
                    : theme.palette.custom.white,
                }}
              >
                <TableCell>
                  <SubscriptionBadge subscription={row.subscription_plan} showAI={row.ai_generated}>
                    <UserAvatar
                      alt={row.account_holder}
                      src={row.image_small || row.image}
                      suspended={row.suspended}
                    />
                  </SubscriptionBadge>
                </TableCell>
                <TableCell>{row.account_holder}</TableCell>
                <TableCell>{row.owner?.user?.email || '-'}</TableCell>
                <TableCell align="center">
                  {getTableTimestamp({ date: row?.created_at, timezone: user?.timezone })}
                </TableCell>
                <TableCell align="center">
                  {getTableTimestamp({ date: row?.updated_at, timezone: user?.timezone })}
                </TableCell>
                <TableCell
                  align="center"
                  // onClick={handleProductClick(product.id, product.account?.id)}
                  sx={{
                    fontSize: '10px',
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <PublicSwitch
                    id={row.id}
                    initValue={row.public}
                    handlePublicChange={handlePublicChange}
                  />
                </TableCell>
                <TableCell>
                  <MoreTableButton
                    items={[
                      {
                        label: row.suspended
                          ? t('accounts-unsuspend-button-text.v')
                          : t('accounts-suspend-button-text.v'),
                        onClick: handleSuspension(row.id, row.suspended),
                      },
                      {
                        label: t('accounts-change-owner.v'),
                        onClick: handleRoleChangeOpen(row.id),
                      },
                      {
                        label: t('subscription-gift-form-submit.v'),
                        onClick: handleSubscriptionGiftOpen(row.id),
                      },
                      {
                        label: t('styles-form-title.v'),
                        onClick: handleCustomCssOpen(row.id),
                      },
                      {
                        label: t('account-prices-label.v'),
                        onClick: handlePricesOpen(row.id),
                      },
                      {
                        label: t('assign-white-label-button-text.v'),
                        onClick: handleModalOpen(row.id),
                      },
                      {
                        label:
                          row.subscription_plan !== EAccountSubscriptionPlans.enterprise
                            ? t('admin-accounts-assign-enterprise-plan-button.v')
                            : t('admin-accounts-unassign-enterprise-plan-button.v'),

                        onClick: handleEnterprise(
                          row.id,
                          row.subscription_plan === EAccountSubscriptionPlans.enterprise,
                        ),
                      },
                    ]}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <AccountOwnerChangeModal accountId={roleChangeId} handleClose={handleRoleChangeClose} />
      <AccountSubscriptionGift
        accountId={subscriptionGiftId}
        handleClose={handleSubscriptionGiftClose}
      />
      <CustomCssModal handleClose={handleCustomCssClose} accountId={customCssId} />
      <AccountPricesModal
        handleClose={handlePricesClose}
        accountId={pricesId}
        open={Boolean(pricesId)}
      />
      <WhiteLabelModal open={modalOpen} handleClose={handleModalClose} accountId={currentRow!} />
    </>
  )
}

export { AccountsTable }
